import type { AsyncStatus } from "@react-hookz/web";
import { useEffect } from "react";

import type { SelectedContentInsight } from "../../../../types/selectedContentInsight";
import { SelectedContentType } from "../../../../types/selectedContentInsight";
import { isNotExecuted, isSuccess } from "../../../../utils/api.utils";
import { InsightFromAICard } from "../insightsFromAI/InsightFromAICard";
import { MarketingNarrativeCard } from "../marketingNarratives/MarketingNarrativeCard";
import { OnDemandInsightCard } from "../onDemandInsights/OnDemandInsightsCard";
import type { RendererProps } from "../withInsightsRenderer";
import { withInsightsRenderer } from "../withInsightsRenderer";

interface InsightsFromAIProps {
    selectedContents: SelectedContentInsight[];
    asyncStatus: AsyncStatus;
    totalResults: number;
    responseId: string;
}

type InsightsFromAIViewerProps = RendererProps & InsightsFromAIProps;

const ContentViewer: React.FunctionComponent<{
    selectedContentInsight: SelectedContentInsight;
    index: number;
    responseId: string;
}> = ({ selectedContentInsight, index, responseId }) => {
    switch (selectedContentInsight.contentType) {
        case SelectedContentType.INSIGHT_WITH_SUMMARY:
            return (
                <InsightFromAICard
                    insight={selectedContentInsight.content}
                    key={index}
                    index={index}
                    responseId={responseId}
                />
            );
        case SelectedContentType.TEMPLATE:
            return (
                <OnDemandInsightCard
                    template={selectedContentInsight.content}
                    index={index}
                    summary={selectedContentInsight.summary}
                    additionalCardDetails={[
                        {
                            label: "Description",
                            value: selectedContentInsight.title,
                            lineClamp: 2,
                        },
                    ]}
                />
            );
        case SelectedContentType.MARKETING_CONTENT:
            return (
                <MarketingNarrativeCard
                    content={selectedContentInsight.content}
                    contentUser={selectedContentInsight.contentUsers}
                    index={index}
                    g4sDetails={{
                        responseId,
                        summary: selectedContentInsight.summary,
                    }}
                    additionalCardDetails={[
                        {
                            label: "Description",
                            value: selectedContentInsight.title,
                            lineClamp: 2,
                        },
                    ]}
                    isExploreWithAI={true}
                />
            );
    }
};

export const InsightsFromAI: React.FunctionComponent<
    InsightsFromAIViewerProps
> = ({
    setAsyncStatus,
    setTotalCount,
    selectedContents,
    asyncStatus,
    totalResults,
    responseId,
}) => {
    useEffect(() => {
        setAsyncStatus(asyncStatus);
    }, [asyncStatus]);

    useEffect(() => {
        if (isSuccess(asyncStatus) || isNotExecuted(asyncStatus)) {
            setTotalCount(totalResults);
        } else {
            setTotalCount(0);
        }
    }, [asyncStatus, totalResults]);

    return (
        <>
            {selectedContents.map((selectedContent, i) => (
                <ContentViewer
                    selectedContentInsight={selectedContent}
                    index={i}
                    responseId={responseId}
                />
            ))}
        </>
    );
};

export const SelectedContentViewer = withInsightsRenderer(InsightsFromAI);
